/**
 * LiveRadioPlayer Component
 * Displays admin live radio broadcast on homepage with embedded stream, 
 * live chat, current track info, and viewer count
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Radio, Send, Users, Music, X, Volume2, VolumeX, 
  Trash2, Ban, MessageCircle, Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extract embed URL from various platform URLs
const getEmbedUrl = (url, platform) => {
  if (!url) return null;
  
  // YouTube
  if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    // Handle various YouTube URL formats
    let videoId = null;
    
    if (url.includes('youtube.com/live/')) {
      videoId = url.split('youtube.com/live/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      return url; // Already an embed URL
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0`;
    }
  }
  
  // Twitch
  if (platform === 'twitch' || url.includes('twitch.tv')) {
    const channel = url.split('twitch.tv/')[1]?.split('?')[0];
    if (channel) {
      return `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}&autoplay=true`;
    }
  }
  
  // If it's already an embed URL or iframe src, return as is
  if (url.includes('embed') || url.includes('player')) {
    return url;
  }
  
  return url;
};

// Chat Message Component
const ChatMessage = ({ message, isAdmin, onDelete, onMute }) => {
  const { user } = useAuth();
  const isOwnMessage = user?.id === message.user_id;
  const canModerate = user?.is_admin && !message.is_admin;
  
  return (
    <div className={`flex gap-2 px-3 py-2 hover:bg-[#1a1a1a]/50 group ${message.is_admin ? 'bg-[#ff0000]/5' : ''}`}>
      {/* Profile Picture */}
      <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 border border-[#1a1a1a]">
        {message.profile_picture ? (
          <img 
            src={`${API}/profile/picture/${message.profile_picture}`} 
            alt="" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
            <span className="text-[#888] text-xs">{message.username?.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
      
      {/* Message Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`font-mono text-xs font-semibold ${
            message.is_admin ? 'text-[#ff0000]' : message.is_vip ? 'text-[#FFD700]' : 'text-[#00CCFF]'
          }`}>
            {message.username}
          </span>
          {message.is_admin && (
            <span className="px-1 py-0.5 bg-[#ff0000] text-white text-[8px] font-bold rounded">HOST</span>
          )}
          {message.is_vip && !message.is_admin && (
            <span className="px-1 py-0.5 bg-[#FFD700] text-black text-[8px] font-bold rounded">VIP</span>
          )}
        </div>
        <p className="text-[#cccccc] font-mono text-xs break-words">{message.message}</p>
      </div>
      
      {/* Moderation Controls */}
      {canModerate && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => onDelete(message.id)}
            className="p-1 hover:bg-[#ff0000]/20 rounded"
            title="Delete message"
          >
            <Trash2 size={12} className="text-[#ff0000]" />
          </button>
          <button 
            onClick={() => onMute(message.user_id, message.username)}
            className="p-1 hover:bg-[#ff0000]/20 rounded"
            title="Mute user"
          >
            <Ban size={12} className="text-[#ff0000]" />
          </button>
        </div>
      )}
    </div>
  );
};

// Main Live Radio Player Component
export const LiveRadioPlayer = () => {
  const { user, token } = useAuth();
  const [liveStatus, setLiveStatus] = useState(null);
  const [viewerId, setViewerId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const chatContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Fetch live radio status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live-radio/status`);
      setLiveStatus(response.data);
      
      // If live, join and fetch chat
      if (response.data.is_live && !viewerId) {
        joinLiveRadio();
      }
    } catch (error) {
      console.error('Failed to fetch live radio status:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [viewerId]);
  
  // Join live radio (track viewer)
  const joinLiveRadio = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/live-radio/join`, {}, { headers });
      setViewerId(response.data.viewer_id);
    } catch (error) {
      console.error('Failed to join live radio:', error);
    }
  };
  
  // Fetch chat messages
  const fetchChat = useCallback(async () => {
    if (!liveStatus?.is_live) return;
    
    try {
      const response = await axios.get(`${API}/live-radio/chat`);
      setMessages(response.data.messages || []);
      
      // Auto-scroll to bottom
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    } catch (error) {
      console.error('Failed to fetch chat:', error);
    }
  }, [liveStatus?.is_live]);
  
  // Send chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token || sending) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/live-radio/chat`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Scroll to bottom
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  // Admin: Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!user?.is_admin) return;
    
    try {
      await axios.delete(
        `${API}/live-radio/admin/chat/${messageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };
  
  // Admin: Mute user
  const handleMuteUser = async (userId, username) => {
    if (!user?.is_admin) return;
    
    try {
      await axios.post(
        `${API}/live-radio/admin/mute/${userId}`,
        { reason: 'Muted by admin' },
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`${username} has been muted`);
    } catch (error) {
      toast.error('Failed to mute user');
    }
  };
  
  // Initial fetch and polling
  useEffect(() => {
    fetchStatus();
    
    // Poll for status every 10 seconds
    const statusInterval = setInterval(fetchStatus, 10000);
    
    return () => {
      clearInterval(statusInterval);
      // Leave live radio on unmount
      if (viewerId) {
        axios.post(`${API}/live-radio/leave`, null, { params: { viewer_id: viewerId } });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, []);
  
  // Poll chat when live
  useEffect(() => {
    if (liveStatus?.is_live) {
      fetchChat();
      pollIntervalRef.current = setInterval(fetchChat, 3000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [liveStatus?.is_live, fetchChat]);
  
  // Don't render if not live
  if (!liveStatus?.is_live) {
    return null;
  }
  
  const embedUrl = getEmbedUrl(liveStatus.stream_url, liveStatus.platform);
  
  return (
    <section className="mb-8" data-testid="live-radio-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Radio size={28} className="text-[#ff0000]" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#ff0000] rounded-full animate-pulse" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-['Chakra_Petch'] text-white uppercase">
              {liveStatus.title || 'Live Radio'}
            </h2>
            <p className="text-[#888888] font-mono text-xs">
              Hosted by {liveStatus.host?.username || 'Admin'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Viewer Count */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#ff0000]/10 border border-[#ff0000]/30">
            <Users size={16} className="text-[#ff0000]" />
            <span className="text-white font-mono text-sm">{liveStatus.viewer_count || 0}</span>
          </div>
          
          {/* Expand/Collapse */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[#888888] hover:text-white"
          >
            {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      {isExpanded && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Video Player */}
          <div className="lg:col-span-2 relative bg-black border border-[#ff0000]/30">
            <div className="aspect-video">
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Live Radio Stream"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a]">
                  <div className="text-center">
                    <Radio size={48} className="text-[#ff0000] mx-auto mb-2 animate-pulse" />
                    <p className="text-[#888888] font-mono text-sm">Stream loading...</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Current Track Overlay */}
            {liveStatus.current_track && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1a1a1a] border border-[#333] flex-shrink-0 overflow-hidden">
                    {liveStatus.current_track.artwork ? (
                      <img 
                        src={liveStatus.current_track.artwork} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music size={18} className="text-[#888888]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-['Chakra_Petch'] text-sm truncate">
                      {liveStatus.current_track.title}
                    </p>
                    <p className="text-[#888888] font-mono text-xs truncate">
                      {liveStatus.current_track.artist}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-[#ff0000] rounded">
                    <Music size={12} className="text-white" />
                    <span className="text-white text-[10px] font-bold uppercase">Now Playing</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Live Chat */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] flex flex-col h-[400px] lg:h-auto">
            {/* Chat Header */}
            <div className="p-3 border-b border-[#1a1a1a] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} className="text-[#00CCFF]" />
                <span className="text-white font-['Chakra_Petch'] text-sm uppercase">Live Chat</span>
              </div>
              <span className="text-[#888888] font-mono text-xs">{messages.length} messages</span>
            </div>
            
            {/* Messages */}
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto"
              style={{ minHeight: '250px', maxHeight: '400px' }}
            >
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[#888888] font-mono text-xs">No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <ChatMessage
                    key={msg.id}
                    message={msg}
                    isAdmin={user?.is_admin}
                    onDelete={handleDeleteMessage}
                    onMute={handleMuteUser}
                  />
                ))
              )}
            </div>
            
            {/* Chat Input */}
            <div className="p-3 border-t border-[#1a1a1a]">
              {token ? (
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Send a message..."
                    className="flex-1 bg-[#111111] border-[#1a1a1a] rounded-none text-white font-mono text-sm h-9"
                    maxLength={500}
                    data-testid="live-chat-input"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="rounded-none bg-[#ff0000] text-white hover:bg-[#cc0000] h-9 px-3"
                    data-testid="live-chat-send"
                  >
                    <Send size={14} />
                  </Button>
                </form>
              ) : (
                <p className="text-[#888888] font-mono text-xs text-center">
                  <a href="/login" className="text-[#00CCFF] hover:underline">Log in</a> to chat
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LiveRadioPlayer;
